/**
 * SummaryCards
 *
 * Top-row metric cards: Scheduled vs Actual hours, Net Hour Variance
 * (amber = over-budget, blue = under-budget), Total WIP $ Variance,
 * Unscheduled Actuals count (warning badge).
 */
import { AlertTriangle, Clock, DollarSign, TrendingUp } from 'lucide-react';

/**
 * @param {{ label: string, value: string, icon: import('react').ComponentType<any>, tone?: 'default'|'amber'|'blue'|'warning' }} props
 */
const MetricCard = ({ label, value, icon: Icon, tone = 'default' }) => {
  const toneClasses = {
    default: 'text-primary',
    amber: 'text-amber-400',
    blue: 'text-accent',
    warning: 'text-rose-400',
  }[tone];

  return (
    <div className="flex flex-1 min-w-[200px] items-start justify-between rounded-lg border border-edge bg-surface p-4 shadow-sm">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted">{label}</p>
        <p className={`mt-2 text-2xl font-semibold ${toneClasses}`}>{value}</p>
      </div>
      <Icon className={`h-5 w-5 ${toneClasses}`} aria-hidden="true" />
    </div>
  );
};

/**
 * @param {{ summary?: {
 *   total_scheduled_hours: number,
 *   total_actual_hours: number,
 *   net_hour_variance: number,
 *   total_wip_cost_variance: number,
 *   unscheduled_actuals_count: number
 * }, isLoading: boolean }} props
 */
export const SummaryCards = ({ summary, isLoading }) => {
  if (isLoading || !summary) {
    return (
      <div className="flex flex-wrap gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-20 flex-1 min-w-[200px] animate-pulse rounded-lg bg-surface" />
        ))}
      </div>
    );
  }

  const varianceIsOverBudget = summary.net_hour_variance > 0;

  return (
    <div className="flex flex-wrap gap-4">
      <MetricCard
        label="Scheduled vs Actual Hours"
        value={`${summary.total_scheduled_hours.toLocaleString()} / ${summary.total_actual_hours.toLocaleString()}`}
        icon={Clock}
      />
      <MetricCard
        label="Net Hour Variance"
        value={`${varianceIsOverBudget ? '+' : ''}${summary.net_hour_variance.toLocaleString()} hrs`}
        icon={TrendingUp}
        tone={varianceIsOverBudget ? 'amber' : 'blue'}
      />
      <MetricCard
        label="Total WIP Value Variance"
        value={summary.total_wip_cost_variance.toLocaleString(undefined, {
          style: 'currency',
          currency: 'USD',
        })}
        icon={DollarSign}
        tone={summary.total_wip_cost_variance >= 0 ? 'amber' : 'blue'}
      />
      <MetricCard
        label="Unscheduled Actuals"
        value={String(summary.unscheduled_actuals_count)}
        icon={AlertTriangle}
        tone={summary.unscheduled_actuals_count > 0 ? 'warning' : 'default'}
      />
    </div>
  );
};
